import { create } from 'zustand';
import type { IntegrationMessageKind } from '../integration-message.types';

const DEFAULT_PAGE_SIZE = 10;

export type IntegrationMessagesFilterState = {
    take: number;
    skip: number;
    searchText: string;
    tenantId: string | null;
    type: string | null;
    id: string | null;
    aggregateId: string | null;
    externalId: string | null;
    isProcessed: boolean | null;
};

const createDefaultFilter = (): IntegrationMessagesFilterState => ({
    take: DEFAULT_PAGE_SIZE,
    skip: 0,
    searchText: '',
    tenantId: null,
    type: null,
    id: null,
    aggregateId: null,
    externalId: null,
    isProcessed: null,
});

type IntegrationMessagesFilterStore = {
    filtersByKind: Record<IntegrationMessageKind, IntegrationMessagesFilterState>;
    setFilter: (
        kind: IntegrationMessageKind,
        filter: Partial<IntegrationMessagesFilterState>,
    ) => void;
    resetFilter: (kind: IntegrationMessageKind) => void;
};

export const useIntegrationMessagesFilterStore = create<IntegrationMessagesFilterStore>((set) => ({
    filtersByKind: {
        inbox: createDefaultFilter(),
        outbox: createDefaultFilter(),
        internalOutbox: createDefaultFilter(),
        emailOutbox: createDefaultFilter(),
    },
    setFilter: (kind, newFilter) => {
        set((state) => ({
            filtersByKind: {
                ...state.filtersByKind,
                [kind]: {
                    ...state.filtersByKind[kind],
                    ...newFilter,
                },
            },
        }));
    },
    resetFilter: (kind) => {
        set((state) => ({
            filtersByKind: {
                ...state.filtersByKind,
                [kind]: createDefaultFilter(),
            },
        }));
    },
}));

export const useIntegrationMessagesFilter = (kind: IntegrationMessageKind) => {
    const filter = useIntegrationMessagesFilterStore((state) => state.filtersByKind[kind]);
    const setFilter = useIntegrationMessagesFilterStore((state) => state.setFilter);
    const resetFilter = useIntegrationMessagesFilterStore((state) => state.resetFilter);

    return {
        filter,
        setFilter: (partial: Partial<IntegrationMessagesFilterState>) => setFilter(kind, partial),
        resetFilter: () => resetFilter(kind),
    };
};
